import {
  ChevronUp,
  CircleArrowUp,
  LogOut,
  Mail,
  Settings,
  Shield,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { ProfileMenuDisplayPrefs } from "@/components/shell/profile-menu-preferences"
import { useUserProfile } from "@/hooks/use-user-profile"
import { useAuth } from "@/lib/auth"
import { supabase } from "@/lib/supabase"
import { icon } from "@/lib/design-tokens"
import { isPaidPlan } from "@/lib/usage-limits"
import { cn } from "@/lib/utils"

const TRIGGER_NAME_MAX = 20

function getInitialsFromEmail(email: string): string {
  const [local] = email.split("@")
  const parts = (local ?? "").split(/[._-]/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  }
  return (local ?? "?").slice(0, 2).toUpperCase()
}

function getInitialsFromFullName(fullName: string | null | undefined): string {
  const trimmed = fullName?.trim()
  if (!trimmed) return ""
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    const a = parts[0]![0]
    const b = parts[parts.length - 1]![0]
    if (a && b) return (a + b).toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

function truncateDisplayName(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name
  return `${name.slice(0, maxLen)}…`
}

/**
 * Profile menu for the app shell sidebar (based on @shadcnblocks/dropdown-menu-profile-3).
 */
export function SidebarProfileDropdown() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { profile } = useUserProfile()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const email = user?.email ?? ""
  const fullName = profile?.full_name?.trim() || ""
  const displayName = fullName || email.split("@")[0] || t("profileMenu.fallbackName")
  const triggerLabel = truncateDisplayName(displayName, TRIGGER_NAME_MAX)

  const initials =
    getInitialsFromFullName(profile?.full_name) ||
    (email ? getInitialsFromEmail(email) : "?")

  const avatarUrl = profile?.avatar_url?.trim() || null
  const paid = isPaidPlan(profile?.plan)
  const isAccountSettings = pathname === "/settings" || pathname.startsWith("/settings/")

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate("/", { replace: true })
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={triggerLabel}
              isActive={isAccountSettings}
              aria-label={t("profileMenu.openMenu")}
            >
              <Avatar className="size-8 rounded-full">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt="" className="rounded-full" />
                ) : null}
                <AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{triggerLabel}</span>
              </span>
              <ChevronUp
                className={cn(icon.md, "ml-auto shrink-0 opacity-60 group-data-[collapsible=icon]:hidden")}
                aria-hidden
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64"
            side="top"
            align="start"
            sideOffset={8}
          >
            <DropdownMenuLabel>{t("profileMenu.settingsSection")}</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => navigate("/settings")}>
                <Settings className={cn(icon.md, "opacity-80")} aria-hidden />
                {t("nav.settings")}
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <ProfileMenuDisplayPrefs />

            {profile?.is_admin === true ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t("admin.sidebarAreaLabel")}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={() => navigate("/admin/visa-metadata")}>
                    <Shield className={cn(icon.md, "opacity-80")} aria-hidden />
                    {t("admin.openAdminPanel")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            ) : null}

            <DropdownMenuSeparator />

            <DropdownMenuLabel>{t("profileMenu.planSection")}</DropdownMenuLabel>
            <div className="px-1.5 pb-1">
              <Badge variant={paid ? "default" : "secondary"} className="font-medium">
                {paid ? t("profileMenu.planPro") : t("profileMenu.planFree")}
              </Badge>
            </div>
            <DropdownMenuGroup>
              {!paid ? (
                <DropdownMenuItem onSelect={() => navigate("/pricing")}>
                  <CircleArrowUp className={cn(icon.md, "opacity-80")} aria-hidden />
                  {t("profileMenu.upgrade")}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuLabel>{t("profileMenu.supportSection")}</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <a href="mailto:feedback@wundrlst.app">
                  <Mail className={cn(icon.md, "opacity-80")} aria-hidden />
                  {t("profileMenu.sendFeedback")}
                </a>
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              variant="destructive"
              onSelect={(e) => {
                e.preventDefault()
                void handleSignOut()
              }}
            >
              <LogOut className={cn(icon.md, "opacity-80")} aria-hidden />
              {t("profileMenu.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
